import { NextSVG, PrevSVG } from '@media/svgs'
import Toggle from '../../../../../components/ui/Toggle'
import RippleButton from '../../../../../components/ui/RippleButton'
import NumericInput from '@components/ui/NumericInput'
import { useFreebetBetting } from '@hooks/betting/useFreebetBetting'
import { TOGGLE, ACTION_VARIANT } from '@constants/bettingConstants'

const isOn = (control) => control === TOGGLE.ENABLED
// Global class that dims + blocks a control for a visible difference.
const lock = (control) => (isOn(control) ? '' : 'disable-actions')

// Sub-label under the bet-style button — PLACED reads as a confirmation because
// the bet is locked in by then.
const BET_BUTTON_DESC = {
  [ACTION_VARIANT.BET]: 'for the next round',
  [ACTION_VARIANT.PLACED]: 'in this round',
}

// One data-driven action button, identical pattern to the Classic card. BET and
// the inert PLACED share the green style; CANCEL and CASHOUT have their own. No
// START_AUTO for freebets.
const ActionButton = ({ variant, label, enabled, onClick, cashoutAmount }) => {
  const lockCls = enabled ? '' : 'disable-actions'

  if (variant === ACTION_VARIANT.CASHOUT) {
    return (
      <RippleButton
        className={`standard-normal-freebet-betting-card-bm-cashout-button-outer-container ${lockCls}`}
        onClick={onClick}
        disabled={!enabled}
      >
        <div className="standard-normal-freebet-betting-card-bm-cashout-button-title">{label}</div>
        <div className="standard-normal-freebet-betting-card-bm-cashout-button-amount">
          {cashoutAmount}
        </div>
      </RippleButton>
    )
  }

  if (variant === ACTION_VARIANT.CANCEL) {
    return (
      <RippleButton
        className={`standard-normal-freebet-betting-card-bm-cancel-button-outer-container ${lockCls}`}
        onClick={onClick}
        disabled={!enabled}
      >
        <div className="standard-normal-freebet-betting-card-bm-cancel-button-title">{label}</div>
      </RippleButton>
    )
  }

  // BET (labelled "Freebet") or the locked-in PLACED badge
  return (
    <RippleButton
      className={`standard-normal-freebet-betting-card-bm-bet-button-outer-container ${lockCls}`}
      onClick={onClick}
      disabled={!enabled}
    >
      <div className="standard-normal-freebet-betting-card-bm-bet-button-title">{label}</div>
      {BET_BUTTON_DESC[variant] && (
        <div className="standard-normal-freebet-betting-card-bm-bet-button-desc">
          {BET_BUTTON_DESC[variant]}
        </div>
      )}
    </RippleButton>
  )
}

const FreebetBettingCard = ({ panel }) => {
  const {
    freebet,
    hasFreebet,
    autoCashout,
    liveCashout,
    actionButton,
    interactionControls,
    expiry,
    renderValue,
    currencyCode,
    prev,
    next,
    toggleAutoCashout,
    onAutoCashoutInput,
    onAutoCashoutBlur,
    onActionButton,
  } = useFreebetBetting(panel)

  const ic = interactionControls

  // Empty slot — no freebet is available for this panel (0 freebets, or the only
  // one is showing on the other panel). This is the "No Freebets available for
  // slot" state; it renders as a message, not a card.
  if (!hasFreebet) {
    return (
      <div className="standard-normal-freebet-betting-card-outer-container">
        <div className="standard-normal-freebet-betting-card-inner-container standard-normal-freebet-betting-card-empty">
          <div className="standard-normal-freebet-betting-card-empty-message">
            No Freebets available for slot
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="standard-normal-freebet-betting-card-outer-container">
      <div className="standard-normal-freebet-betting-card-inner-container">
        <div className="standard-normal-freebet-betting-card-stake-module-container">
          <RippleButton
            className={`standard-normal-freebet-betting-card-stake-control-button ${lock(ic.prevArrow)}`}
            onClick={prev}
            disabled={!isOn(ic.prevArrow)}
          >
            <PrevSVG width={'18px'} height={'18px'} />
          </RippleButton>
          <div className="standard-normal-freebet-betting-card-stake-input">
            <div
              className="standard-normal-freebet-betting-card-stake-input-label"
              title={freebet.tag}
            >
              {freebet.tagShort}
              {currencyCode ? ` (${currencyCode})` : ''}
            </div>
            <div className="standard-normal-freebet-betting-card-stake-input-box">
              {freebet.amount}
            </div>
          </div>
          <RippleButton
            className={`standard-normal-freebet-betting-card-stake-control-button ${lock(ic.nextArrow)}`}
            onClick={next}
            disabled={!isOn(ic.nextArrow)}
          >
            <NextSVG width={'18px'} height={'18px'} />
          </RippleButton>
        </div>
        {freebet.rawExpiringAt && (
          <div className="standard-normal-freebet-betting-card-expiry-time-module-container">
            <div
              className={`standard-normal-freebet-betting-card-expiry-time-box ${expiry.isExpired ? 'expired' : ''}`}
            >
              {expiry.isExpired ? 'Expired' : `Expires in ${expiry.label}`}
            </div>
          </div>
        )}
        <div className="standard-normal-freebet-betting-card-button-module-container">
          <ActionButton
            variant={actionButton.variant}
            label={actionButton.label}
            enabled={actionButton.enabled}
            onClick={onActionButton}
            cashoutAmount={liveCashout != null ? renderValue(liveCashout) : ''}
          />
        </div>
        <div className="standard-normal-freebet-betting-card-extra-controls-module-container">
          <div className="standard-normal-freebet-betting-card-extra-control-box">
            <div className="standard-normal-freebet-betting-card-extra-control-box-title">
              Auto Cashout
            </div>
            <div className="standard-normal-freebet-betting-card-extra-control-box-toggle">
              <Toggle
                isChecked={autoCashout.status === TOGGLE.ENABLED}
                onCheck={toggleAutoCashout}
                disabled={!isOn(ic.autoCashout)}
                className={lock(ic.autoCashout)}
              />
            </div>
            <div
              className={`standard-normal-freebet-betting-card-extra-control-box-input ${lock(ic.autoCashout)}`}
            >
              <NumericInput
                compact
                className="standard-normal-freebet-betting-card-extra-control-box-input-box"
                value={autoCashout.value ?? ''}
                onChange={(event) => onAutoCashoutInput(event.target.value)}
                onBlur={onAutoCashoutBlur}
                disabled={!isOn(ic.autoCashout) || autoCashout.status !== TOGGLE.ENABLED}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FreebetBettingCard
